import { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, {
    clearSessionExpiryState,
    SESSION_EXPIRED_EVENT,
} from './axios';  // Import the configured Axios instance
import {
    changeGoogleTranslateLanguage,
} from '../../component/GoogleTranslate';

const AuthContext = createContext();

function getTokenExpiration(token) {
    try {
        const tokenParts = token.split('.');

        if (tokenParts.length !== 3) {
            return null;
        }

        const encodedPayload = tokenParts[1]
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        const paddedPayload = encodedPayload.padEnd(
            Math.ceil(encodedPayload.length / 4) * 4,
            '='
        );

        const payload = JSON.parse(
            atob(paddedPayload)
        );

        return payload.exp
            ? payload.exp * 1000
            : null;
    } catch {
        return null;
    }
}

export const AuthProvider = ({ children }) => {

    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [role, setRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isNewUser, setIsNewUser] = useState(false)
    const [googleReady, setGoogleReady] = useState(false);
    const [googleLoginError, setGoogleLoginError] = useState('');
    const googleLoginInProgressRef = useRef(false);
    const logoutTimerRef = useRef(null);
    const clearAutoLogout = useCallback(() => {
        if (logoutTimerRef.current) {
            window.clearTimeout(
                logoutTimerRef.current
            );

            logoutTimerRef.current = null;
        }
    }, []);
    const logout = useCallback(() => {
        clearAutoLogout();
        clearSessionExpiryState();

        localStorage.removeItem('_token');
        localStorage.removeItem('_refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('role');

        changeGoogleTranslateLanguage('en');

        setUser(null);
        setRole(null);
        setIsNewUser(false);

        navigate('/', {
            replace: true,
        });
    }, [clearAutoLogout, navigate]);

    const setAutoLogout = useCallback(
        (token) => {
            clearAutoLogout();

            const expirationTime =
                getTokenExpiration(token);

            if (!expirationTime) {
                return;
            }

            const scheduleLogout = () => {
                const remainingTime =
                    expirationTime - Date.now();

                if (remainingTime <= 0) {
                    logout();
                    return;
                }

                // Browsers cannot reliably accept a timeout
                // longer than this value.
                const maximumTimeout =
                    2_147_483_647;

                logoutTimerRef.current =
                    window.setTimeout(
                        scheduleLogout,
                        Math.min(
                            remainingTime,
                            maximumTimeout
                        )
                    );
            };

            scheduleLogout();
        },
        [
            clearAutoLogout,
            logout,
        ]
    );
    useEffect(() => {
        const handleSessionExpired = () => {
            logout();
        };

        window.addEventListener(
            SESSION_EXPIRED_EVENT,
            handleSessionExpired
        );

        return () => {
            window.removeEventListener(
                SESSION_EXPIRED_EVENT,
                handleSessionExpired
            );
        };
    }, [logout]);

    useEffect(() => {
        return () => {
            clearAutoLogout();
        };
    }, [clearAutoLogout]);

    useEffect(() => {
        const token = localStorage.getItem('_token');
        const storedUser = localStorage.getItem('user');
        const storedRole = localStorage.getItem('role');

        let parsedUser = null;

        try {
            parsedUser = storedUser
                ? JSON.parse(storedUser)
                : null;
        } catch {
            localStorage.removeItem('user');
        }

        if (token) {
            setUser(parsedUser);
            setRole(storedRole);
            setLoading(false);
            setAutoLogout(token);
            return;
        }

        setLoading(false);
    }, [setAutoLogout]);
    // User register api
    const register = async (full_name, email, password) => {
        try {
            const response = await axios.post('/auth/register', { full_name, email, password });
            const token = response.data.authorization;

            localStorage.setItem('_token', token);
            clearSessionExpiryState();
            setAutoLogout(token);

            if (response.data && response.data.user) {
                localStorage.setItem('user', JSON.stringify(response.data.user));
            }

            changeGoogleTranslateLanguage(
                response.data.user?.interface_language ||
                'en'
            );
            localStorage.setItem('role', 'user');

            setRole('user')
            setUser(response.data.user)
            setIsNewUser(true)

            navigate('/welcome')
        }
        catch (error) {
            const errorMessage = error.response ? error.response.data : 'An error occurred';
            return errorMessage;
        }
    }

    // User login api
    const login = async (email, password) => {
        try {
            const response = await axios.post('/auth/login', {
                email,
                password,
            });

            const token = response.data.authorization;
            const authenticatedUser = response.data.user;
            const authenticatedRole = response.data.role;

            if (authenticatedRole !== 'user') {
                return 'This account type is not supported.';
            }

            setUser(authenticatedUser);
            setRole(authenticatedRole);

            localStorage.setItem('_token', token);
            clearSessionExpiryState();
            setAutoLogout(token);

            if (authenticatedUser) {
                localStorage.setItem(
                    'user',
                    JSON.stringify(authenticatedUser)
                );
            }

            changeGoogleTranslateLanguage(
                authenticatedUser?.interface_language ||
                'en'
            );

            localStorage.setItem('role', authenticatedRole);

            navigate('/opportunity-calculator', {
                state: true,
            });

            return undefined;
        } catch (error) {
            return getAuthErrorMessage(error);
        }
    };

    // User login with google api
    const getAuthErrorMessage = (error) => {
        if (error?.response?.data?.message) return error.response.data.message;
        if (error?.response?.data?.error) return error.response.data.error;
        if (error?.message) return error.message;
        return 'An error occurred';
    };

    const login_with_google = useCallback(async (credential) => {
        try {
            const response = await axios.post('/auth/login-with-google', { credential });

            const token = response.data.authorization;
            const user = response.data?.user;
            const role = response.data?.role || 'user';

            if (!token || !user) {
                return 'Invalid login response from server';
            }

            localStorage.setItem('_token', token);
            clearSessionExpiryState();
            setAutoLogout(token);
            localStorage.setItem('role', role);
            localStorage.setItem('user', JSON.stringify(user));
            navigate('/opportunity-calculator', {
                replace: true,
            });

            setUser(user);
            setRole(role);
            setAutoLogout(token);

            if (response.data.isNewCreated) {
                setIsNewUser(true);
                navigate('/welcome');
            } else {
                setIsNewUser(false);
                navigate('/opportunity-calculator', { state: true });
            }

            return null;
        } catch (error) {
            return getAuthErrorMessage(error);
        }
    }, [navigate, setAutoLogout]);
    useEffect(() => {
        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

        if (!clientId) {
            setGoogleLoginError('Google sign-in is not configured.');
            return;
        }

        let intervalId;
        let cancelled = false;

        // Keep the latest React login function available to Google's one global callback.
        window.__leedanaGoogleLoginHandler = async (response) => {
            if (googleLoginInProgressRef.current) return;

            if (!response?.credential) {
                setGoogleLoginError('Google sign-in failed. Please try again.');
                return;
            }

            googleLoginInProgressRef.current = true;
            setGoogleLoginError('');

            const error = await login_with_google(response.credential);

            googleLoginInProgressRef.current = false;

            if (error) {
                setGoogleLoginError(error);
            }
        };

        const initializeGoogle = () => {
            if (cancelled || !window.google?.accounts?.id) {
                return false;
            }

            // Google has already been configured in this browser page.
            if (window.__leedanaGoogleGsiInitialized) {
                setGoogleReady(true);
                return true;
            }

            window.google.accounts.id.initialize({
                client_id: clientId,
                callback: (response) => window.__leedanaGoogleLoginHandler?.(response),
            });

            window.__leedanaGoogleGsiInitialized = true;
            setGoogleReady(true);

            return true;
        };

        let timeoutId;

        if (!initializeGoogle()) {
            intervalId = window.setInterval(() => {
                if (initializeGoogle()) {
                    clearInterval(intervalId);
                    clearTimeout(timeoutId);
                }
            }, 100);

            timeoutId = window.setTimeout(() => {
                clearInterval(intervalId);
                setGoogleLoginError(
                    'Google sign-in could not be loaded. Please refresh the page.'
                );
            }, 10000);
        }

        return () => {
            cancelled = true;
            clearInterval(intervalId);
            clearTimeout(timeoutId);
        };
    }, [login_with_google]);

    const markUserAsNotNew = () => {
        setIsNewUser(false);
    }

    return (
        <AuthContext.Provider value={{
            user, role, loading, login, logout, register, isNewUser, markUserAsNotNew, login_with_google, googleReady,
            googleLoginError
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
